import { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { RotateCcw, Trash2, Loader2, Plus } from "lucide-react";

/** Clases a recuperar del alumno, con alta manual para planes contratados antes de la app. */
export const ReposicionesAlumno = ({ alumnoId }) => {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ fecha_origen: "", cantidad: 1, nota: "", caduca: "" });
  const [enviando, setEnviando] = useState(false);

  const load = useCallback(() => {
    api.get(`/reposiciones?alumno_id=${alumnoId}`).then(({ data }) => setItems(data)).catch(() => {});
  }, [alumnoId]);
  useEffect(() => { load(); }, [load]);

  const pendientes = items.filter((r) => r.estado === "pendiente");

  const guardar = async () => {
    setEnviando(true);
    try {
      const { data } = await api.post("/reposiciones", { alumno_id: alumnoId, ...form,
        cantidad: Number(form.cantidad) || 1 });
      toast.success(`${data.creadas} clase(s) a recuperar añadidas`
        + (data.caduca ? ` · válidas hasta el ${data.caduca}` : ""));
      setForm({ fecha_origen: "", cantidad: 1, nota: "", caduca: "" });
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail), { duration: 9000 });
    } finally {
      setEnviando(false);
    }
  };

  const borrar = async (id) => {
    try {
      await api.delete(`/reposiciones/${id}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <Card className="rounded-2xl p-6 border-border shadow-sm" data-testid="card-reposiciones">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <RotateCcw className="h-5 w-5 text-primary" />
          <h3 className="font-display text-lg font-bold">Clases a recuperar</h3>
        </div>
        <Badge variant={pendientes.length ? "default" : "secondary"} data-testid="reposiciones-badge">
          {pendientes.length} pendiente(s)
        </Badge>
      </div>

      <div className="space-y-2">
        {items.map((r) => (
          <div key={r.id} className="flex items-start justify-between gap-3 text-sm border-b border-border pb-2 last:border-0"
            data-testid={`reposicion-item-${r.id}`}>
            <span>
              <span className="font-semibold">Clase perdida del {r.fecha_origen}</span>
              {r.origen === "manual" && <span className="text-muted-foreground"> · alta manual</span>}
              {r.caduca && <span className="text-muted-foreground"> · válida hasta {r.caduca}</span>}
              {r.nota && <span className="text-muted-foreground"> · {r.nota}</span>}
              <span className="block text-xs text-muted-foreground mt-0.5">
                {r.estado === "pendiente" ? "Pendiente de agendar" : `Agendada (${r.estado})`}
                {r.creado_por ? ` · ${r.creado_por}` : ""}
              </span>
            </span>
            <button onClick={() => borrar(r.id)} data-testid={`reposicion-borrar-${r.id}`}>
              <Trash2 className="h-4 w-4 text-muted-foreground hover:text-destructive transition-colors duration-200" />
            </button>
          </div>
        ))}
        {items.length === 0 && (
          <p className="text-sm text-muted-foreground" data-testid="reposiciones-vacio">
            No tiene clases pendientes de recuperar.
          </p>
        )}
      </div>

      <p className="text-xs text-muted-foreground mt-4">
        Se agendan desde Calendario → «Reposiciones pendientes». Las de alta manual pueden agendarse
        en cualquier clase de su sede.
      </p>

      <Button className="rounded-full font-semibold mt-4 w-full" onClick={() => setOpen(true)}
        data-testid="reposicion-anadir-btn">
        <Plus className="h-4 w-4 mr-2" /> Añadir clases a recuperar
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl max-h-[85vh] overflow-y-auto" data-testid="reposicion-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Clases a recuperar</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4">
            <div className="grid gap-2">
              <Label>Fecha de la clase perdida</Label>
              <Input type="date" value={form.fecha_origen} data-testid="reposicion-fecha-input"
                onChange={(e) => setForm({ ...form, fecha_origen: e.target.value })} />
            </div>
            <div className="grid gap-2">
              <Label>Nº de clases</Label>
              <Input type="number" min={1} max={20} value={form.cantidad} data-testid="reposicion-cantidad-input"
                onChange={(e) => setForm({ ...form, cantidad: e.target.value })} />
            </div>
            <div className="grid gap-2">
              <Label>Válidas hasta (opcional)</Label>
              <Input type="date" value={form.caduca} data-testid="reposicion-caduca-input"
                onChange={(e) => setForm({ ...form, caduca: e.target.value })} />
              <p className="text-xs text-muted-foreground">
                Si lo dejas vacío se usa el fin de su bono o de su periodo pagado.
              </p>
            </div>
            <div className="grid gap-2">
              <Label>Motivo o nota</Label>
              <Textarea value={form.nota} data-testid="reposicion-nota-input"
                placeholder="Faltas del curso anterior pendientes de recuperar"
                onChange={(e) => setForm({ ...form, nota: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button className="rounded-full font-semibold" onClick={guardar}
              disabled={enviando || !form.fecha_origen} data-testid="reposicion-guardar-btn">
              {enviando ? <Loader2 className="h-4 w-4 animate-spin" /> : "Añadir"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};
